/*
 * Ecranul 1: intrarea in aplicatie.
 *
 * Butoanele de identitate federata vin din `GET /shop/auth/sso/providers`, nu
 * din configurarea aplicatiei. Asa spune contractul si are dreptate: identitatea
 * ramane in portal, iar pagina deseneaza ce i se spune ca exista. Un buton scris
 * de mana aici ar trebui schimbat la fiecare client care porneste sau opreste un
 * IdP, adica o livrare noua pentru o setare de configurare.
 */

import { useState } from 'react';
import cecLogo from '../assets/cec-logo.svg';
import odLogo from '../assets/optimumdesk-logo.png';
import { toApiError } from '../api/errors';
import * as shop from '../api/shop';
import { useAuth } from '../auth/AuthContext';
import { InlineAlert } from '../components/states';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';
import { isNative, nativeReturnUrl, openExternal } from '../lib/platform';
import { ExternalLink } from '../components/ExternalLink';
import { privacyUrl } from '../lib/privacy';
import { bindStartUrl } from '../auth/ssoBinding';

/*
 * Adresa de pornire a autentificarii federate, cu locul de intoarcere cerut.
 *
 * Pe web nu se cere nimic: portalul foloseste adresa lui implicita, din
 * SHOP_SSO_RETURN_URL, exact ca inainte.
 *
 * Pe nativ se cere schema proprie. Portalul o accepta doar daca e trecuta,
 * caracter cu caracter, in SHOP_SSO_RETURN_URLS - verificarea de acolo e
 * `in_array` strict tocmai ca sa nu existe un redirect deschis care sa care un
 * bilet de autentificare. Daca nu e trecuta, portalul refuza si omul vede
 * mesajul de eroare, nu un drum tacut catre nicaieri.
 */
function ssoHref(startUrl: string): string {
  if (nativeReturnUrl === null) return startUrl;

  const separator = startUrl.includes('?') ? '&' : '?';

  return startUrl + separator + 'return=' + encodeURIComponent(nativeReturnUrl);
}

export function LoginScreen() {
  const { signIn, ssoError, dismissSsoError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const providers = useAsync((signal) => shop.ssoProviders({ signal }), []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    dismissSsoError();
    setError(null);

    if (username.trim() === '' || password === '') {
      setError(t('Please fill in both your username and your password.'));
      return;
    }

    setBusy(true);
    try {
      await signIn(username.trim(), password);
      /* Nu se navigheaza de aici. Rutarea se uita la starea de autentificare si
         schimba singura ecranul; o navigare in plus ar concura cu ea. */
    } catch (cause) {
      /* Contractul cere acelasi mesaj indiferent daca utilizatorul exista sau
         nu, si il trimite gata scris. Se afiseaza asa cum vine. */
      setError(toApiError(cause).message);
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login">
        {/*
          Doua marci, si ordinea nu e intamplatoare: institutia careia ii
          apartine catalogul sta prima, furnizorul platformei al doilea, mai
          mic. Omul care se autentifica trebuie sa recunoasca intai unde e.

          Fisierele sunt in proiect, nu cerute de pe internet la fiecare
          incarcare: reteaua bancii e inchisa, iar o imagine de pe un domeniu
          din afara pur si simplu n-ar aparea.
        */}
        <div className="login__brand">
          <img className="login__logo-client" src={cecLogo} alt="CEC Bank" />

          <span className="login__brand-divider" aria-hidden="true" />

          <span className="login__brand-text">
            {/* Marca, pe doua randuri, dintr-o singura traducere taiata la `|`.
                Doua chei separate nu merg: a doua ar fi fost chiar "Catalog",
                si aia e deja eticheta din meniu - traducand-o pentru marca, se
                schimba si acolo. Cu bara, taietura sta in traducere, unde si
                trebuie: in engleza cade dupa "IT Service", in romana dupa
                "Catalog de". */}
            {t('IT Service|Catalog')
              .split('|')
              .map((line, i) => (
                <span className={`brand__line${i + 1}`} key={line}>
                  {line}
                </span>
              ))}
          </span>
        </div>

        <h1 className="login__title">{t('Sign in')}</h1>

        <div className="panel login__panel">
          {ssoError ? (
            <InlineAlert>
              <strong>{t('Single sign-on did not complete.')}</strong> {ssoError.message}{' '}
              <span className="alert__code">{ssoError.code}</span>
            </InlineAlert>
          ) : null}

          {error ? <InlineAlert>{error}</InlineAlert> : null}

          <form onSubmit={submit} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="login-username">{t('Username')}</label>
              <input
                id="login-username"
                className="field__control"
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="login-password">{t('Password')}</label>
              <input
                id="login-password"
                className="field__control"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
              {busy ? t('Signing in…') : t('Sign in')}
            </button>
          </form>

          <SsoBlock
            status={providers.status}
            providers={providers.data?.providers ?? []}
            errorCode={providers.error?.code ?? null}
          />
        </div>

        <p className="login__foot">
            {t('Access is granted by your company administrator. Contact the service desk if you cannot sign in.')}
          </p>

        {/* Politica de confidentialitate, si aici, nu doar in bara laterala.
            Aici e momentul in care conteaza: omul nu s-a autentificat inca, deci
            e singurul ecran pe care il vede inainte sa-si dea datele. Ceruta si
            de magazine ca legatura accesibila din aplicatie. */}
        {privacyUrl() ? (
          <p className="login__legal">
            <ExternalLink href={privacyUrl()}>{t('Privacy policy')}</ExternalLink>
          </p>
        ) : null}

        {/* Marca furnizorului, jos si discreta. Cine se autentifica are nevoie
            sa stie unde e, nu cine a construit - de-asta nu sta langa marca
            institutiei, ci sub linia de final. */}
        <p className="login__vendor">
          <span className="login__vendor-label">{t('Powered by')}</span>
          <img src={odLogo} alt="OptimumDesk" />
        </p>
      </div>
    </div>
  );
}

function SsoBlock({
  status,
  providers,
  errorCode,
}: {
  status: string;
  providers: { slug: string; label: string; protocol: string; start_url: string }[];
  errorCode: string | null;
}) {
  if (status === 'loading') {
    return <p className="login__sso-note">{t('Checking single sign-on…')}</p>;
  }

  if (status === 'error') {
    /* Daca lista de furnizori cade, autentificarea cu parola trebuie sa ramana
       folosibila. Se spune ce s-a intamplat, nu se blocheaza ecranul. */
    return (
      <>
        <Divider>single sign-on</Divider>
        <InlineAlert>
          {t('Sign-in providers could not be loaded')}
          {errorCode ? ` (${errorCode})` : ''}
          {'. '}
          {t('You can still sign in with your username and password.')}
        </InlineAlert>
      </>
    );
  }

  /* Niciun IdP configurat nu e o eroare - se ascunde sectiunea intreaga. */
  if (providers.length === 0) return null;

  return (
    <>
      <Divider>{t('or continue with')}</Divider>
      <div className="sso-list">
        {providers.map((provider) => (
          /*
           * Legatura duce in portal, pe `start_url` asa cum vine din api.
           * Portalul intoarce omul aici cu un bilet in adresa, iar schimbul se
           * face la pornire - vezi auth/ssoTicket.ts.
           */
          <a
            className="btn btn--block sso"
            key={provider.slug}
            href={ssoHref(provider.start_url)}
            onClick={(event) => {
              /*
               * Pe web navigarea ramane in aceeasi fila, iar portalul intoarce
               * omul inapoi aici.
               *
               * In carcasa nativa nu exista "inapoi aici" - de aceea `ssoHref`
               * a adaugat deja `?return=` cu schema proprie, iar drumul se
               * deschide intr-un browser in-app. Cand IdP-ul termina, portalul
               * redirecteaza catre schema, sistemul trezeste aplicatia si
               * biletul ajunge prin `onDeepLink` - vezi auth/ssoTicket.ts.
               */
              /*
               * Pe amandoua, adresa pleaca cu amprenta secretului de legare
               * (auth/ssoBinding.ts). Calculul ei e asincron (WebCrypto), deci
               * clicul se opreste si navigarea o face codul. Clicul cu mijlocul
               * sau "deschide in fila noua" merg pe `href`, fara legare - acolo
               * fila noua n-ar avea oricum secretul.
               */
              if (
                !isNative &&
                (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
              ) {
                return;
              }

              event.preventDefault();
              void (async () => {
                const url = await bindStartUrl(ssoHref(provider.start_url));
                if (isNative) {
                  await openExternal(url);
                } else {
                  window.location.assign(url);
                }
              })();
            }}
          >
            {/* Eticheta vine din api, dar cheia de traducere e chiar textul
                englezesc - deci trece prin `t` ca orice alt text. Un furnizor
                nou, cu o eticheta pe care n-o stim, ramane in engleza si atat:
                nu se strica nimic, doar nu e tradus inca. */}
            <span className="sso__label">{t(provider.label || provider.slug)}</span>
            <span className="sso__proto micro-label">{provider.protocol}</span>
          </a>
        ))}
      </div>
    </>
  );
}

function Divider({ children }: { children: React.ReactNode }) {
  return <p className="divider">{children}</p>;
}
